import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getConversationFeedback,
  getMessageFeedback,
  listMessages,
  runQualityCheck,
  saveConversationFeedback,
  saveMessageFeedback,
  sendReply,
  setConversationMode,
} from '../api/adminApi.js';
import type {
  AdminConversation,
  AdminMessage,
  AdminQualityCheck,
  ConversationFeedback,
  ConversationMode,
  MessageFeedback,
  MessageFeedbackInput,
} from '../api/types.js';

export interface UseActiveConversationResult {
  messages: AdminMessage[];
  conversationAiCost: { totalCny: number; turns: number };
  hasMoreMessages: boolean;
  loading: boolean;
  error: string | null;
  messageFeedback: Record<number, MessageFeedback>;
  conversationFeedback: ConversationFeedback | null;
  quality: AdminQualityCheck | null;
  sending: boolean;
  loadMessages: (friendId: number) => Promise<void>;
  loadOlderMessages: (friendId: number) => Promise<void>;
  loadConversationFeedback: (conversationId: number) => Promise<void>;
  updateMessages: (friendId: number, message: AdminMessage) => void;
  appendMessage: (friendId: number, message: AdminMessage) => void;
  setQuality: (friendId: number, quality: AdminQualityCheck) => void;
  setMode: (friendId: number, mode: ConversationMode) => Promise<AdminConversation | undefined>;
  handleSendReply: (conversation: AdminConversation, text: string) => Promise<void>;
  handleRunQualityCheck: (friendId: number) => Promise<void>;
  handleSaveMessageFeedback: (messageId: number, input: MessageFeedbackInput) => Promise<void>;
  handleSaveConversationFeedback: (conversationId: number, summary: string) => Promise<void>;
  clear: () => void;
}

export function useActiveConversation(): UseActiveConversationResult {
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [conversationAiCost, setConversationAiCost] = useState({ totalCny: 0, turns: 0 });
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageFeedback, setMessageFeedback] = useState<Record<number, MessageFeedback>>({});
  const [conversationFeedback, setConversationFeedback] = useState<ConversationFeedback | null>(null);
  const [quality, setQualityState] = useState<AdminQualityCheck | null>(null);
  const [sending, setSending] = useState(false);
  const activeFriendIdRef = useRef<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadMessages = useCallback(async (friendId: number) => {
    activeFriendIdRef.current = friendId;
    setLoading(true);
    setError(null);
    setQualityState(null);
    setMessageFeedback({});
    setConversationFeedback(null);
    try {
      const data = await listMessages(friendId, 100, 0);
      if (mounted.current && activeFriendIdRef.current === friendId) {
        setMessages(data.messages || []);
        setConversationAiCost(data.aiCost ?? { totalCny: 0, turns: 0 });
        setHasMoreMessages(data.hasMore === true);
        await loadFeedbackForConversation(data.messages || []);
      }
    } catch (err) {
      if (mounted.current && activeFriendIdRef.current === friendId) {
        setError(err instanceof Error ? err.message : '加载消息失败');
      }
    } finally {
      if (mounted.current && activeFriendIdRef.current === friendId) {
        setLoading(false);
      }
    }
  }, []);

  const loadConversationFeedback = useCallback(async (conversationId: number) => {
    try {
      const data = await getConversationFeedback(conversationId);
      if (mounted.current) {
        setConversationFeedback(data.feedback || null);
      }
    } catch {
      if (mounted.current) setConversationFeedback(null);
    }
  }, []);

  const loadFeedbackForConversation = async (msgs: AdminMessage[]) => {
    const validMessages = msgs.filter((m) => Number.isFinite(Number(m.id)));
    const entries = await Promise.all(
      validMessages.map(async (message) => {
        try {
          const data = await getMessageFeedback(Number(message.id));
          return [Number(message.id), data.feedback] as const;
        } catch {
          return [Number(message.id), null] as const;
        }
      }),
    );
    if (!mounted.current) return;
    const feedbackMap: Record<number, MessageFeedback> = {};
    for (const [messageId, feedback] of entries) {
      if (feedback) feedbackMap[messageId] = feedback;
    }
    setMessageFeedback(feedbackMap);
  };

  const updateMessages = useCallback((friendId: number, message: AdminMessage) => {
    if (activeFriendIdRef.current !== friendId) return;
    setMessages((prev) => {
      const index = prev.findIndex((m) => m.id === message.id);
      if (index >= 0) {
        const next = [...prev];
        next[index] = message;
        return next;
      }
      return prev;
    });
  }, []);

  const appendMessage = useCallback((friendId: number, message: AdminMessage) => {
    if (activeFriendIdRef.current !== friendId) return;
    setMessages((prev) => {
      const exists = prev.some((m) => m.id === message.id);
      if (exists) {
        return prev.map((m) => (m.id === message.id ? message : m));
      }
      return [...prev, message];
    });
  }, []);

  const setQuality = useCallback((friendId: number, value: AdminQualityCheck) => {
    if (activeFriendIdRef.current !== friendId) return;
    setQualityState(value);
  }, []);

  const setMode = useCallback(async (friendId: number, mode: ConversationMode) => {
    try {
      const data = await setConversationMode(friendId, mode);
      return data.conversation;
    } catch (err) {
      setError(err instanceof Error ? err.message : '切换模式失败');
      return undefined;
    }
  }, []);

  const handleSendReply = useCallback(async (conversation: AdminConversation, text: string) => {
    const trimmed = text.trim();
    if (!trimmed || conversation.mode !== 'manual') return;

    const optimistic: AdminMessage = {
      id: `pending-${Date.now()}`,
      conversationId: conversation.id,
      direction: 'out',
      senderWechatId: conversation.botWechatId,
      content: trimmed,
      msgType: 1,
      mediaType: null,
      mediaUrl: null,
      mediaPath: null,
      mediaTranscript: null,
      mediaMetadata: null,
      status: 'pending',
      apolloMessageId: null,
      thinking: null,
      createdAt: new Date().toISOString(),
    };

    setSending(true);
    appendMessage(conversation.friendId, optimistic);

    try {
      const data = await sendReply(conversation.friendId, trimmed);
      updateMessages(conversation.friendId, data.message);
    } catch (err) {
      const failed: AdminMessage = { ...optimistic, status: 'failed' };
      updateMessages(conversation.friendId, failed);
      setError(err instanceof Error ? err.message : '发送失败');
    } finally {
      if (mounted.current) setSending(false);
    }
  }, [appendMessage, updateMessages]);

  const handleRunQualityCheck = useCallback(async (friendId: number) => {
    try {
      const data = await runQualityCheck(friendId);
      setQualityState(data.qualityCheck);
    } catch (err) {
      setError(err instanceof Error ? err.message : '质检失败');
    }
  }, []);

  const handleSaveMessageFeedback = useCallback(async (messageId: number, input: MessageFeedbackInput) => {
    try {
      const data = await saveMessageFeedback(messageId, input);
      setMessageFeedback((prev) => ({ ...prev, [messageId]: data.feedback }));
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存反馈失败');
      throw err;
    }
  }, []);

  const handleSaveConversationFeedback = useCallback(async (conversationId: number, summary: string) => {
    try {
      const data = await saveConversationFeedback(conversationId, summary);
      setConversationFeedback(data.feedback);
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存评语失败');
      throw err;
    }
  }, []);

  const loadOlderMessages = useCallback(async (friendId: number) => {
    if (activeFriendIdRef.current !== friendId || !hasMoreMessages) return;
    try {
      const data = await listMessages(friendId, 100, messages.length);
      if (mounted.current && activeFriendIdRef.current === friendId) {
        setMessages((prev) => {
          const seen = new Set(prev.map((message) => message.id));
          return [...(data.messages || []).filter((message) => !seen.has(message.id)), ...prev];
        });
        setConversationAiCost(data.aiCost ?? { totalCny: 0, turns: 0 });
        setHasMoreMessages(data.hasMore === true);
      }
    } catch (err) {
      if (mounted.current && activeFriendIdRef.current === friendId) {
        setError(err instanceof Error ? err.message : '加载更早记录失败');
      }
    }
  }, [hasMoreMessages, messages.length]);

  const clear = useCallback(() => {
    activeFriendIdRef.current = null;
    setMessages([]);
    setConversationAiCost({ totalCny: 0, turns: 0 });
    setHasMoreMessages(false);
    setMessageFeedback({});
    setConversationFeedback(null);
    setQualityState(null);
    setError(null);
  }, []);

  return {
    messages,
    conversationAiCost,
    hasMoreMessages,
    loading,
    error,
    messageFeedback,
    conversationFeedback,
    quality,
    sending,
    loadMessages,
    loadOlderMessages,
    loadConversationFeedback,
    updateMessages,
    appendMessage,
    setQuality,
    setMode,
    handleSendReply,
    handleRunQualityCheck,
    handleSaveMessageFeedback,
    handleSaveConversationFeedback,
    clear,
  };
}
